/**
 * CAP-SSE-1e — Events live stream helpers (pure, no DOM).
 * ZD-TRANSPORT one-stream + ZD-PARTIAL-F13 duals freeze:
 *   Normative Live = GET /events/stream (v1) — FE always prefers v1;
 *   Soft Soft = GET /events/stream/v2 remains coded (gateway Soft Soft) but not Soft-prefer;
 *   Soft delayed = ~30s poll when capacity / gateway unavailable (Scheme C).
 */

import { buildEventsStreamPath } from "./telemetry-console-api.js";
import { pumpSseBody } from "./sse.js";

/** Poll interval when live SSE unavailable (design: ~30s). */
export const EVENTS_SSE_POLL_INTERVAL_MS = 30000;

/** Consecutive stream failures before entering poll mode (non-503 errors). */
export const EVENTS_SSE_FAIL_THRESHOLD = 3;

/**
 * @param {number} status HTTP status from stream GET
 * @param {string} [detailText] response body snippet
 */
export function shouldEnterPollFallback(status, detailText) {
  const st = Number(status) || 0;
  if (st === 503 || st === 429) return true;
  const t = String(detailText || "").toLowerCase();
  return (
    t.includes("subscribers at capacity") ||
    t.includes("sse gateway unavailable") ||
    t.includes("gateway subscribers")
  );
}

/** @param {string | null | undefined} retryAfterHeader */
export function parseRetryAfterSeconds(retryAfterHeader) {
  const raw = String(retryAfterHeader || "").trim();
  if (!raw) return 0;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return Math.floor(n);
  const d = Date.parse(raw);
  if (Number.isFinite(d)) return Math.max(0, Math.floor((d - Date.now()) / 1000));
  return 0;
}

/** @param {Record<string, string | number>} [query] */
export function buildEventsStreamV2Path(query) {
  const q = new URLSearchParams();
  if (query && typeof query === "object") {
    for (const [k, v] of Object.entries(query)) {
      if (v != null && v !== "") q.set(k, String(v));
    }
  }
  const s = q.toString();
  return s ? `/events/stream/v2?${s}` : "/events/stream/v2";
}

/**
 * Prefer v2 when gateway operational on this replica (from GET /health).
 * @param {Record<string, string | number>} query
 * @param {boolean} preferV2
 */
export function pickEventsStreamPath(query, preferV2) {
  return preferV2 ? buildEventsStreamV2Path(query) : buildEventsStreamPath(query);
}

/**
 * ZD-PARTIAL-F13 — product FE always opens Live v1.
 * Soft Soft gateway may still be ON host (`sse_gateway.enabled`); Soft prefer is frozen OFF.
 * Soft Soft tooling may call ``pickEventsStreamPath(query, true)`` explicitly.
 * @param {{ sse_gateway?: { enabled?: boolean; operational?: boolean } } | null | undefined} _health
 */
export function preferSseGatewayV2Stream(_health) {
  return false;
}

export function pollFallbackBadgeLabel() {
  return "Live delayed";
}

/** @param {number} retryAfterSec */
export function pollFallbackBannerMessage(retryAfterSec) {
  const ra = Math.max(0, Number(retryAfterSec) || 0);
  if (ra > 0) {
    return `Live stream unavailable (capacity). Refreshing every 30s. Retry live in ~${ra}s.`;
  }
  return "Live stream unavailable. Refreshing every 30s (delayed mode).";
}

export function openEventsLiveStream({ url, token = "", onOpen, onEvent, onFallback }) {
  const ac = new AbortController();
  const stream = {
    readyState: 0,
    close() {
      stream.readyState = 2;
      ac.abort();
    },
  };
  void (async () => {
    try {
      const headers = { Accept: "text/event-stream" };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(url, { credentials: "include", headers, signal: ac.signal });
      if (!response.ok || !response.body) {
        const detail = await response.text().catch(() => "");
        onFallback?.(response.status, detail);
        stream.close();
        return;
      }
      stream.readyState = 1;
      onOpen?.();
      await pumpSseBody(response.body.getReader(), ac.signal, (_kind, raw) => {
        if (!raw) return;
        try {
          const event = JSON.parse(raw);
          if (event && event.event_type !== "hello") onEvent?.(event);
        } catch (_) {}
      });
      if (!ac.signal.aborted) onFallback?.(0, "stream ended");
    } catch (error) {
      if (!ac.signal.aborted) onFallback?.(0, String((error && error.message) || error || ""));
    } finally {
      stream.readyState = 2;
    }
  })();
  return stream;
}
